import {
  LocalParticipant as TwilioLocalParticipant,
  LocalVideoTrack as TwilioLocalVideoTrack,
} from "twilio-video";
import { logger } from "../../../datadog/logger";
import { getStoredBackgroundSettings } from "../../core/config";
import {
  AudioVideoSettings,
  BackgroundType,
  LocalAVTrackContextAtom,
  LocalMediaStatus,
} from "../../core/types";
import { publishLocalAudioVideoTrack } from "../tracks/publishLocalAudioVideoTrack";
import { TrackOperationResult } from "../tracks/types";
import { initializeBackground } from "../tracks/video/initializeBackground";

export enum ChangeCameraStatusCode {
  "CHANGED" = "changed",
  "BACKGROUND_ERROR" = "background_error",
  "ERROR" = "error",
}

export type SetCameraResult = TrackOperationResult & {
  code: ChangeCameraStatusCode;
  track?: TwilioLocalVideoTrack;
};

export async function setCamera(
  cameraDeviceId: string,
  getLocalVideoTrack: () => LocalAVTrackContextAtom,
  setStoredAudioVideoSettings: (
    updater: (prev: AudioVideoSettings) => AudioVideoSettings,
  ) => void,
  setHasBackgroundProcessorFailure: (value: boolean) => void,
  localParticipant?: TwilioLocalParticipant,
): Promise<SetCameraResult> {
  const { success, track, status } = await publishLocalAudioVideoTrack(
    "video",
    getLocalVideoTrack,
    {
      trackOptions: { deviceId: { exact: cameraDeviceId } },
    },
    setStoredAudioVideoSettings,
    localParticipant,
  );
  const publishedTrack = track as TwilioLocalVideoTrack | undefined;

  if (!success) {
    logger.error("Unable to change camera device");

    return {
      success: false,
      status,
      track: publishedTrack,
      code: ChangeCameraStatusCode.ERROR,
    };
  }

  const backgroundSettings = getStoredBackgroundSettings();
  const hasBackground =
    backgroundSettings.backgroundType &&
    backgroundSettings.backgroundType !== BackgroundType.NONE;

  if (hasBackground) {
    const {
      success: initializeBackgroundSuccess,
      track: videoTrackWithBackground,
    } = await initializeBackground(
      backgroundSettings,
      setStoredAudioVideoSettings,
      publishedTrack,
    );

    setHasBackgroundProcessorFailure(!initializeBackgroundSuccess);

    if (!initializeBackgroundSuccess) {
      logger.error("Unable to set background after changing camera device");

      return {
        success: false,
        status: LocalMediaStatus.ERROR,
        track: publishedTrack,
        code: ChangeCameraStatusCode.BACKGROUND_ERROR,
      };
    }

    return {
      success: true,
      status: LocalMediaStatus.READY,
      track: videoTrackWithBackground
        ? (videoTrackWithBackground as TwilioLocalVideoTrack)
        : publishedTrack,
      code: ChangeCameraStatusCode.CHANGED,
    };
  }

  return {
    success: true,
    status: LocalMediaStatus.READY,
    track: publishedTrack,
    code: ChangeCameraStatusCode.CHANGED,
  };
}
